/**
 * Utility functions for simulation API calls with usage limit handling
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export interface UsageLimitError {
  error: string;
  upgrade_required: boolean;
}

export interface SimulationResponse {
  simulation_id: number;
  message: string;
  status: string;
}

export class UsageLimitExceededError extends Error {
  public readonly upgrade_required = true;
  
  constructor(message: string) {
    super(message);
    this.name = 'UsageLimitExceededError';
  }
}

/**
 * Create a new simulation with proper error handling for usage limits
 */
export const createSimulation = async (simulationData: any): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(simulationData)
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || 'Failed to start simulation');
  }
  
  return data;
};

/**
 * Rerun an existing simulation with proper error handling for usage limits
 */
export const rerunSimulation = async (simulationId: number): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations/${simulationId}/rerun`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || 'Failed to rerun simulation');
  }
  
  return data;
};

/**
 * Parse questions with proper error handling for usage limits
 */
export const parseQuestions = async (
  questions: string[], 
  goal: string = '', 
  context: string = '',
  question_images?: Record<number, string[]>,
  advanced_parsing: boolean = false
): Promise<{task_id: string; questions_count: number}> => {
  const response = await fetch(`${API_URL}/parse-questions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      questions,
      goal,
      context,
      question_images,
      advanced_parsing
    })
  });

  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return data;
};


/**
 * Handle API errors consistently across forms
 */
export const handleApiError = (error: unknown, setError: (message: string) => void): void => {
  if (error instanceof UsageLimitExceededError) {
    setError(error.message);
  } else if (error instanceof Error) {
    setError(error.message);
  } else {
    setError('An unexpected error occurred. Please try again.');
  }
};

/**
 * Generate AI descriptions for a list of image paths
 * Used when users edit images in parsed questions
 */
export const generateImageDescriptions = async (imagePaths: string[]): Promise<{
  success: boolean;
  descriptions: string[];
  processed_count: number;
}> => {
  const response = await fetch(`${API_URL}/api/question-images/generate-descriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ image_paths: imagePaths })
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Failed to generate descriptions');
  }
  
  return data;
};